import { useCallback, useMemo } from "react"

import lodash from "lodash"
import { useParams } from "react-router-dom"

import {
    Layer,
    MapStyles,
    ROADWORK_CLICKABLE_LAYER,
    ROADWORK_LAYER,
    ROADWORK_SOURCE,
    SELECTED_LINE_LAYER_SIZE,
    getInterpolatedLineLayerWidth,
    useMapDispatchContext,
    useMapStateContext,
    useOrderingLayers,
} from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { ROADWORK_LAYER_WIDTH, getRoadworkTechniquesLayerStyle } from "../../constants/getRoadworkConfig"
import { useRoadworksStateContext } from "../../contexts/RoadworksContext"

import { RoadworksSource } from "../RoadworksSource"

export function RoadworkTechniquesLayer() {

    const theme = useTheme()
    const { getCurrentMapStyle } = useMapStateContext()
    const { setMapPin } = useMapDispatchContext()
    const { selectedLinearLocations } = useNetworksStateContext()
    const { filters, selectedTechnique } = useRoadworksStateContext()
    const { road, roadworkUuid } = useParams()
    const navigate = useNavigateWithSearchParams()
    const selectedRoad = selectedLinearLocations?.[0]?.road

    const roadworksLayerConfig = useMemo(() => {
        const referentialColor = getCurrentMapStyle() === MapStyles.plan ? theme.palette.map.darkReferential
            : theme.palette.map.lightReferential

        return lodash.merge(
            getRoadworkTechniquesLayerStyle(referentialColor, selectedTechnique, selectedRoad, filters, theme),
            {
                "paint": {
                    "line-width": getInterpolatedLineLayerWidth(ROADWORK_LAYER_WIDTH),
                },
            },
        )
    }, [getCurrentMapStyle, filters, selectedRoad, selectedTechnique, theme])

    const roadworksLayerClickConfig = useMemo(() => {
        return lodash.merge(
            getRoadworkTechniquesLayerStyle("transparent", selectedTechnique, selectedRoad, filters, theme),
            {
                "paint": {
                    "line-color": "transparent",
                    "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
                },
            })
    }, [filters, selectedRoad, selectedTechnique, theme])

    const handleLayerClick = useCallback((event) => {
        setMapPin()
        const linearLocation = JSON.parse(event.feature.properties.linearLocation)[0]
        navigate((roadworkUuid || road) ? `../${linearLocation.road}/${event.feature.properties.uuid}`
            : `${linearLocation.road}/${event.feature.properties.uuid}`)
    }, [navigate, road, roadworkUuid])

    useOrderingLayers([ROADWORK_CLICKABLE_LAYER, ROADWORK_LAYER])

    return <RoadworksSource id={ROADWORK_SOURCE}>
        <Layer
            {...roadworksLayerConfig}
            id={ROADWORK_LAYER}
        />
        <Layer
            {...roadworksLayerClickConfig}
            id={ROADWORK_CLICKABLE_LAYER}
            onClick={handleLayerClick}
        />
    </RoadworksSource>
}